export const W = 360;
export const H = 540;
export const DURATION = 30;
export const MAX_SCORE = 5000;
const PADDLE_W = 76;
const PADDLE_H = 14;
const PADDLE_Y = H - 40;

export const createState = () => ({
  paddleX: W / 2,
  targetX: W / 2,
  items: [],
  particles: [],
  texts: [],
  blasts: [],
  score: 0,
  streak: 0,
  bestStreak: 0,
  timeLeft: DURATION,
  spawnIn: 0.4,
  shake: 0,
  flash: 0,
});

export const multiplier = (streak) => Math.min(5, 1 + Math.floor(streak / 4));

const pickKind = () => {
  const r = Math.random();
  if (r < 0.08) return 'gem';
  if (r < 0.33) return 'bomb';
  return 'orb';
};

const spawn = (s, progress) => {
  const kind = pickKind();
  s.items.push({
    kind,
    x: 20 + Math.random() * (W - 40),
    y: -20,
    r: kind === 'gem' ? 11 : 13,
    vy: 170 + progress * 260 + Math.random() * 70,
    rot: Math.random() * Math.PI,
  });
};

const burst = (s, x, y, color, n = 10) => {
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const v = 60 + Math.random() * 140;
    s.particles.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 0.5, color });
  }
};

const popText = (s, x, y, text, color) => s.texts.push({ x, y, text, color, life: 0.8 });

const COLORS = { orb: '#38BDF8', gem: '#FACC15', bomb: '#F43F5E' };
const FIRE = ['#F97316', '#FACC15', '#EF4444', '#FDE68A'];

const explode = (s, x, y, big) => {
  s.blasts.push({ x, y, t: 0, dur: big ? 0.6 : 0.45, size: big ? 1 : 0.65 });
  const n = big ? 26 : 14;
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const v = 80 + Math.random() * (big ? 260 : 160);
    s.particles.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 60, life: 0.7, color: FIRE[i % FIRE.length], size: 3 + Math.random() * 4 });
  }
};

const catchItem = (s, it, sfx) => {
  if (it.kind === 'bomb') {
    s.score = Math.max(0, s.score - 50);
    s.streak = 0;
    s.shake = 0.3;
    s.flash = 0.25;
    popText(s, it.x, it.y - 20, '-50', COLORS.bomb);
    explode(s, it.x, it.y, true);
    sfx('bomb');
    return;
  }
  const pts = it.kind === 'gem' ? 100 : 10 * multiplier(s.streak);
  s.streak += 1;
  s.bestStreak = Math.max(s.bestStreak, s.streak);
  s.score = Math.min(MAX_SCORE, s.score + pts);
  popText(s, it.x, it.y, `+${pts}`, COLORS[it.kind]);
  sfx(it.kind);
  burst(s, it.x, it.y, COLORS[it.kind]);
};

export const step = (s, dt, sfx) => {
  s.timeLeft = Math.max(0, s.timeLeft - dt);
  const progress = 1 - s.timeLeft / DURATION;
  s.paddleX += (s.targetX - s.paddleX) * Math.min(1, dt * 18);
  s.spawnIn -= dt;
  if (s.spawnIn <= 0) {
    spawn(s, progress);
    s.spawnIn = 0.55 - progress * 0.33;
  }
  const half = PADDLE_W / 2;
  s.items = s.items.filter((it) => {
    it.y += it.vy * dt;
    it.rot += dt * 3;
    const hit = it.y + it.r >= PADDLE_Y && it.y - it.r <= PADDLE_Y + PADDLE_H
      && it.x >= s.paddleX - half - it.r * 0.5 && it.x <= s.paddleX + half + it.r * 0.5;
    if (hit) { catchItem(s, it, sfx); return false; }
    if (it.kind === 'bomb' ? it.y >= H - 12 : it.y - it.r > H) {
      if (it.kind !== 'bomb') s.streak = 0;
      else explode(s, it.x, H - 12, false);
      return false;
    }
    return true;
  });
  stepFx(s, dt);
};

export const finale = (s, sfx) => {
  s.items.forEach((it) => (it.kind === 'bomb' ? explode(s, it.x, it.y, true) : burst(s, it.x, it.y, COLORS[it.kind], 6)));
  if (s.items.some((it) => it.kind === 'bomb')) { s.shake = 0.4; sfx('bomb'); }
  s.items = [];
};

export const stepFx = (s, dt) => {
  s.particles = s.particles.filter((p) => {
    p.x += p.vx * dt; p.y += p.vy * dt; p.vy += 300 * dt; p.life -= dt;
    return p.life > 0;
  });
  s.blasts = s.blasts.filter((b) => { b.t += dt; return b.t < b.dur; });
  s.texts = s.texts.filter((t) => { t.y -= 40 * dt; t.life -= dt; return t.life > 0; });
  s.shake = Math.max(0, s.shake - dt);
  s.flash = Math.max(0, s.flash - dt);
};

const drawItem = (ctx, it) => {
  ctx.save();
  ctx.translate(it.x, it.y);
  ctx.shadowColor = COLORS[it.kind];
  ctx.shadowBlur = 16;
  ctx.fillStyle = COLORS[it.kind];
  if (it.kind === 'gem') {
    ctx.rotate(it.rot);
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const rr = i % 2 ? it.r * 0.45 : it.r * 1.15;
      const a = (i / 10) * Math.PI * 2;
      ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
    }
    ctx.closePath();
    ctx.fill();
  } else if (it.kind === 'bomb') {
    ctx.rotate(Math.sin(it.rot) * 0.25);
    ctx.shadowColor = '#F97316';
    ctx.font = `${it.r * 2.3}px ${EMOJI_FONT}`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('💣', 0, 0);
  } else {
    ctx.beginPath(); ctx.arc(0, 0, it.r, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.beginPath(); ctx.arc(-4, -4, it.r * 0.35, 0, Math.PI * 2); ctx.fill();
  }
  ctx.restore();
};

const EMOJI_FONT = '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';

const drawBlast = (ctx, b) => {
  const p = b.t / b.dur;
  const r = (18 + p * 70) * b.size;
  ctx.save();
  ctx.globalAlpha = 1 - p;
  ctx.strokeStyle = '#FDE68A'; ctx.lineWidth = 6 * (1 - p) + 1;
  ctx.beginPath(); ctx.arc(b.x, b.y, r, 0, Math.PI * 2); ctx.stroke();
  const g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, r * 0.8);
  g.addColorStop(0, 'rgba(255,237,160,0.95)'); g.addColorStop(0.5, 'rgba(249,115,22,0.7)'); g.addColorStop(1, 'rgba(239,68,68,0)');
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(b.x, b.y, r * 0.8, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = Math.min(1, (1 - p) * 1.6);
  ctx.font = `${(30 + p * 34) * b.size}px ${EMOJI_FONT}`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('💥', b.x, b.y);
  ctx.restore();
};

export const draw = (ctx, s) => {
  ctx.save();
  if (s.shake > 0) ctx.translate((Math.random() - 0.5) * 10 * s.shake * 3, (Math.random() - 0.5) * 10 * s.shake * 3);
  ctx.fillStyle = '#0B1020';
  ctx.fillRect(-10, -10, W + 20, H + 20);
  ctx.strokeStyle = 'rgba(148,163,184,0.07)';
  ctx.lineWidth = 1;
  for (let x = 0; x <= W; x += 30) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = 0; y <= H; y += 30) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
  s.items.forEach((it) => drawItem(ctx, it));
  s.blasts.forEach((b) => drawBlast(ctx, b));
  s.particles.forEach((p) => {
    ctx.globalAlpha = Math.max(0, p.life * 2);
    ctx.fillStyle = p.color;
    const sz = p.size || 4;
    ctx.fillRect(p.x - sz / 2, p.y - sz / 2, sz, sz);
  });
  ctx.globalAlpha = 1;
  ctx.font = 'bold 18px "Space Mono", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  s.texts.forEach((t) => {
    ctx.globalAlpha = Math.min(1, t.life * 1.5);
    ctx.fillStyle = t.color;
    ctx.fillText(t.text, t.x, t.y);
  });
  ctx.globalAlpha = 1;
  ctx.shadowColor = '#A3E635';
  ctx.shadowBlur = 18;
  ctx.fillStyle = '#A3E635';
  ctx.beginPath();
  ctx.roundRect(s.paddleX - PADDLE_W / 2, PADDLE_Y, PADDLE_W, PADDLE_H, 7);
  ctx.fill();
  ctx.shadowBlur = 0;
  if (s.flash > 0) {
    ctx.fillStyle = `rgba(244,63,94,${s.flash})`;
    ctx.fillRect(0, 0, W, H);
  }
  ctx.restore();
};
